import React from 'react';
import PropTypes from 'prop-types';
import {Formik, Field, Form, ErrorMessage} from 'formik';
/* Components */
import Modal from '../../common/modal/modal';
import RadioButton from '../../common/form-elements/radio-button/radio-button';
import Input from '../../common/form-elements/input/input';
import Textarea from '../../common/form-elements/textarea/textarea';
import FormErrorMessage from '../../common/form-elements/form-error-message/form-error-message';
import Avatar from '../../common/avatar/avatar';
import Checkbox from '../../common/form-elements/checkbox/checkbox';
import ImageCropper from '../../common/image-cropper';
/* Helpers */
import {isAuthorizedAction} from '../../../helpers/dashboard-helpers';
/* Validations */
import {
  schemaValidate,
  required,
  minLength,
  maxLength,
  imageUpload,
} from '../../../helpers/validation-helpers';
import './settings-modal.scss';

class DashboardSettingsModal extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      modalActiveStep: 'createNewDashboard',
      uploadedImage: null,
    };
  }

  closeAndDiscard = () => {
    this.props.onClose();
    this.setState({
      modalActiveStep: '',
    });
  };

  handleStepAction = (values, formikBag) => {
    const {modalActiveStep} = this.state;
    if (modalActiveStep === 'createNewDashboard') {
      this.setState({
        modalActiveStep: 'dashboardImage',
      });
      formikBag.setSubmitting(false);
    }
    if (modalActiveStep === 'dashboardImage') {
      this.setState({
        modalActiveStep: 'permissionSettings',
      });
      formikBag.setSubmitting(false);
    }
    if (modalActiveStep === 'permissionSettings') {
      return this.onSubmit(values);
    }
    return null;
  };

  onSubmit = (values) => {
    this.setState(
      {
        modalActiveStep: '',
      },
      () => {
        this.props.onSave(values);
      }
    );
  };

  handleImageUpload = (image) => {
    this.setState({
      uploadedImage: image,
    });
  };

  handleImageUploadCancel = () => {
    this.setState({
      uploadedImage: null,
    });
  };

  handleImageCrop = (props) => (image) => {
    props.setFieldValue('image', image);
    this.setState({
      uploadedImage: null,
    });
  };

  getDashboardModalForm = (props) => {
    const modalActiveStep = this.state.modalActiveStep;
    if (modalActiveStep === 'createNewDashboard') {
      return (
        <>
          <div className="glu-form-row">
            <label className="glu-label">Dashboard name</label>
            <Field
              as={Input}
              placeholder="Enter dashboard name"
              autoFocus
              name="dashboardName"
              onChange={props.handleChange('dashboardName')}
              validate={schemaValidate([required(), minLength(), maxLength(50)])}
              invalid={props.errors.dashboardName && props.touched.dashboardName}
              className="glu-form-input-pad"
            />
            <ErrorMessage name="dashboardName" component={FormErrorMessage} />
          </div>
          <div className="glu-form-row">
            <label className="glu-label">Description</label>
            <Field
              as={Textarea}
              onChange={props.handleChange('dashboardDescription')}
              placeholder="Enter dashboard description"
              name="dashboardDescription"
              validate={schemaValidate(maxLength(250))}
              invalid={props.errors.dashboardDescription && props.touched.dashboardDescription}
              maxChar={250}
              className="glu-form-input-pad"
            />
            <ErrorMessage name="dashboardDescription" component={FormErrorMessage} />
          </div>
        </>
      );
    }
    if (modalActiveStep === 'dashboardImage') {
      return (
        <>
          {this.props.imageUploadOptions &&
            this.props.imageUploadOptions.map((option) => (
              <div className="glu-form-row" key={option.imageOption}>
                <Field
                  as={RadioButton}
                  name="selectedImageOptionId"
                  value={option.imageOption}
                  onChange={props.handleChange}
                  isChecked={option.imageOption === props.values.selectedImageOptionId}
                >
                  <div className="glu-radio__image-setting">{option.label}</div>
                </Field>
              </div>
            ))}
          {props.values.selectedImageOptionId === 'DEFAULT' && (
            <div className="glu-form-row">
              <div className="glu-flex-row glu-flex-grow glu-flex-align-center glu-col-pad--lg-big">
                <div className="glu-flex-shrink">
                  <Avatar imageSrc={this.props.defaultImage} size="lg" />
                </div>
                <div className="glu-flex-grow glu-h5 text-secondary">
                  A dashboard image can help you quickly choose a dashboard from the dashboard
                  library.
                </div>
              </div>
            </div>
          )}
          {props.values.selectedImageOptionId === 'DISABLED' && (
            <div className="glu-form-row">
              <div className="glu-flex-row glu-flex-grow glu-flex-align-center glu-col-pad--lg-big">
                <div className="glu-flex-shrink">
                  <Avatar size="lg" imageSrc={null} isEmpty />
                </div>
                <div className="glu-flex-grow glu-h5 text-secondary">
                  A dashboard image can help you quickly choose a dashboard from the dashboard
                  library.
                </div>
              </div>
            </div>
          )}
          {props.values.selectedImageOptionId === 'CUSTOM' && (
            <div className="glu-form-row">
              <div className="glu-flex-row glu-flex-grow glu-flex-align-center glu-col-pad--lg-big">
                <div className="glu-flex-shrink">
                  <Field
                    as={Avatar}
                    name="image"
                    handleImageUpload={this.handleImageUpload}
                    validate={schemaValidate(imageUpload())}
                    size="lg"
                    isEditable
                    imageSrc={props.values.image}
                  />
                  <ErrorMessage name="image" component={FormErrorMessage} />
                </div>
                <div className="glu-flex-grow glu-h5 text-secondary">
                  A dashboard image can help you quickly choose a dashboard from the dashboard
                  library.
                </div>
              </div>
            </div>
          )}
          <Modal.Default isOpen={!!this.state.uploadedImage}>
            <ImageCropper
              imageSource={this.state.uploadedImage}
              handleCancel={this.handleImageUploadCancel}
              handleCrop={this.handleImageCrop(props)}
            />
          </Modal.Default>
        </>
      );
    }
    if (modalActiveStep === 'permissionSettings') {
      return (
        <>
          {isAuthorizedAction('publicDashboardWrite') &&
            this.props.permissionSettingsOptions.map((option) => (
              <div className="glu-form-row" key={option.id}>
                <Field
                  as={RadioButton}
                  name="selectedPermissionSettingsId"
                  value={option.id}
                  onChange={props.handleChange}
                  className="glu-radio__permission"
                  isChecked={option.id === Number(props.values.selectedPermissionSettingsId)}
                >
                  <div className="glu-radio__permission-title">{option.title}</div>
                  <p className="glu-radio__permission-info text-secondary">
                    {option.description ? option.description : null}
                  </p>
                </Field>
              </div>
            ))}
          <div className="glu-form-row">
            <Field
              as={Checkbox}
              name="isShared"
              className="glu-checkbox__permission"
              onChange={props.handleChange}
              isChecked={props.values.isShared}
            >
              <div className="glu-radio__permission-title">
                Make this dashboard also available in child organizations
              </div>
            </Field>
          </div>
        </>
      );
    }
    return null;
  };

  render() {
    const modalActiveStep = this.state.modalActiveStep;
    if (!modalActiveStep) {
      return null;
    }
    let modalTitle = 'Create new dashboard';
    if (modalActiveStep === 'dashboardImage') {
      modalTitle = 'Select dashboard image';
    }
    if (modalActiveStep === 'permissionSettings') {
      modalTitle = 'Permission settings';
    }
    return (
      <Modal.Default isOpen={!!modalActiveStep}>
        <div className="glu-modal__header glu-flex glu-flex-align-center">
          <div className="glu-modal__title glu-flex-grow">{modalTitle}</div>
          <div className="glu-flex-shrink">
            <button
              aria-label="close"
              type="button"
              className="glu-btn-link g-icon g-icon-close"
              onClick={this.closeAndDiscard}
            />
          </div>
        </div>
        <Formik
          initialValues={{
            dashboardName: '',
            dashboardDescription: '',
            selectedImageOptionId: 'DEFAULT',
            selectedPermissionSettingsId: 1,
            isShared: false,
            image: '',
          }}
          onSubmit={this.handleStepAction}
        >
          {(props) => {
            return (
              <Form>
                <div className="glu-modal__body">
                  <div className="glu-content__section">{this.getDashboardModalForm(props)}</div>
                </div>
                <footer className="glu-modal__footer">
                  <div className="glu-flex-grow glu-flex-row">
                    <div className="glu-flex-grow text-right">
                      <button
                        type="button"
                        onClick={this.closeAndDiscard}
                        className="glu-btn glu-btn--secondary"
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="glu-flex-shrink">
                      <button type="submit" className="glu-btn glu-btn--primary">
                        Next
                      </button>
                    </div>
                  </div>
                </footer>
              </Form>
            );
          }}
        </Formik>
      </Modal.Default>
    );
  }
}

DashboardSettingsModal.propTypes = {
  defaultImage: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  imageUploadOptions: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  permissionSettingsOptions: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
};

export default DashboardSettingsModal;
